import { useEffect, useState } from "react";
import api from "@/lib/api";
import { X, UserCog, History } from "lucide-react";
import { toast } from "sonner";

export default function AssignAgentModal({ customer, onClose, onSaved }) {
  const [agents, setAgents] = useState([]);
  const [history, setHistory] = useState([]);
  const [f, setF] = useState({
    agent_id: customer.agent_id || "",
    secondary_agent_id: customer.secondary_agent_id || "",
    assignment_start: customer.assignment_start || new Date().toISOString().slice(0, 10),
    assignment_end: customer.assignment_end || "",
    assignment_status: customer.assignment_status || "Active",
    territory: customer.territory || customer.state || "",
    reason: "",
  });

  useEffect(() => {
    api.get("/agents").then(r => setAgents(r.data)).catch(() => {});
    api.get(`/customers/${customer.id}/assignments`).then(r => setHistory(r.data)).catch(() => {});
  }, [customer.id]);

  const save = async () => {
    try {
      await api.post(`/customers/${customer.id}/assign`, f);
      toast.success("Assignment saved");
      onSaved && onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4">
      <div className="bg-white rounded-xl w-full max-w-lg max-h-[92vh] overflow-y-auto" data-testid="assign-agent-modal">
        <div className="p-5 border-b flex justify-between">
          <h3 className="text-xl font-heading font-semibold flex items-center gap-2"><UserCog className="w-5 h-5 text-[#003DA5]" /> Assign Agent — {customer.name}</h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <F label="Primary Agent *">
            <select value={f.agent_id} onChange={e => setF({ ...f, agent_id: e.target.value })} className="w-full h-11 px-3 border rounded-lg" data-testid="assign-primary">
              <option value="">— unassigned —</option>{agents.map(a => <option key={a.id} value={a.id}>{a.name} ({a.email})</option>)}
            </select>
          </F>
          <F label="Secondary / Support Agent (optional)">
            <select value={f.secondary_agent_id || ""} onChange={e => setF({ ...f, secondary_agent_id: e.target.value })} className="w-full h-11 px-3 border rounded-lg" data-testid="assign-secondary">
              <option value="">— none —</option>{agents.filter(a => a.id !== f.agent_id).map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Assignment Start"><input type="date" value={f.assignment_start} onChange={e => setF({ ...f, assignment_start: e.target.value })} className="w-full h-11 px-3 border rounded-lg" data-testid="assign-start" /></F>
            <F label="Assignment End (optional)"><input type="date" value={f.assignment_end} onChange={e => setF({ ...f, assignment_end: e.target.value })} className="w-full h-11 px-3 border rounded-lg" data-testid="assign-end" /></F>
            <F label="Territory / State"><input value={f.territory} onChange={e => setF({ ...f, territory: e.target.value })} className="w-full h-11 px-3 border rounded-lg" data-testid="assign-territory" /></F>
            <F label="Status">
              <select value={f.assignment_status} onChange={e => setF({ ...f, assignment_status: e.target.value })} className="w-full h-11 px-3 border rounded-lg" data-testid="assign-status">
                <option>Active</option><option>Inactive</option><option>Temporary</option>
              </select>
            </F>
          </div>
          <F label="Reason for reassignment"><textarea value={f.reason} onChange={e => setF({ ...f, reason: e.target.value })} rows={2} className="w-full p-3 border rounded-lg" data-testid="assign-reason" /></F>

          {history.length > 0 && (
            <div className="border-t pt-3">
              <div className="text-xs uppercase font-semibold text-slate-500 mb-2 flex items-center gap-1"><History className="w-3 h-3" /> Previous Assignments</div>
              <ul className="text-xs space-y-1 max-h-40 overflow-y-auto">
                {history.map(h => {
                  const prevA = agents.find(a => a.id === h.previous.agent_id)?.name || "—";
                  const newA = agents.find(a => a.id === h.new.agent_id)?.name || "—";
                  return (
                    <li key={h.id} className="p-2 rounded bg-[#F3F7FC] border border-slate-100" data-testid={`hist-${h.id}`}>
                      <div><span className="text-slate-500">{prevA}</span> → <span className="font-semibold">{newA}</span> {h.reason && <span className="italic text-slate-500">— {h.reason}</span>}</div>
                      <div className="text-slate-500 text-[10px]">by {h.changed_by_name} • {new Date(h.changed_at).toLocaleString()}</div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
        <div className="p-5 border-t flex justify-end gap-2">
          <button onClick={onClose} className="h-11 px-5 border rounded-lg">Cancel</button>
          <button onClick={save} className="mlily-btn h-11 px-5 rounded-lg font-semibold" data-testid="assign-save-btn">Save Assignment</button>
        </div>
      </div>
    </div>
  );
}

const F = ({ label, children }) => <div><label className="text-xs uppercase tracking-wider font-semibold text-slate-500">{label}</label><div className="mt-1">{children}</div></div>;
